import { Box, Checkbox, Flex, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleFlag } from 'react-circle-flags'
import { createRenderProfiler } from '../../../lib/performance/monitoring'
import { getCountryNameByCode } from '../../countries/PhoneDial'
import TableRowItem from './TableRowItem'

export type TableType =
  | 'people'
  | 'teams'
  | 'jobs'
  | 'skills'
  | 'aboutorg'
  | 'documents'
  | 'content'
  | 'paths'
  | 'assets'
  | 'evaluations'
  | 'past-requests'
  | 'leave-requests'
  | 'leave-balances'
  | 'attendance'
  | 'public-holidays'
  | 'time-tracking-projects'
  | 'time-tracking-members'
  | 'time-tracking-activities'
  | 'accountability-areas'
  | 'roles'

export interface Column {
  label: string
  accessor: string
  type: 'string' | 'actions' | 'select' | 'custom'
  width?: string
  Cell?: React.FC<{ row: any }>
  MobileCell?: React.FC<{ row: any }>
  mobileLabel?: string
  mobilePriority?: number
  hideOnMobileCard?: boolean

  image?: string
  icon?: React.FC
  colors?: {
    bg: string
    text: string
    border: string
  }
}

export interface RowData {
  [key: string]: any
}

interface CustomTableProps {
  columns: Column[]
  data: RowData[]
  onRowClick?: (row: RowData) => void
  disableMobileRowClick?: boolean
  onCopyLink?: (row: RowData) => void
  onOpenNewTab?: (row: RowData) => void
  onSearch?: (row: RowData) => void
  onRowIntent?: (row: RowData) => void
  selectedRows: (number | string)[]
  onSelectAll: (isChecked: boolean) => void
  onSelectRow: (id: number | string, isChecked: boolean) => void
  allSelected: boolean
  isIndeterminate: boolean
  tableType: TableType
}

const renderMobileValue = (column: Column, row: RowData, emptyLabel: string) => {
  if (column.MobileCell) return <column.MobileCell row={row} />
  if (['country', 'country_of_work', 'country_of_residence', 'nationality'].includes(column.accessor)) {
    const countryValue = row[column.accessor]
    if (!countryValue) return emptyLabel
    const countryCode = String(countryValue)
    const countryName = getCountryNameByCode(countryCode) || countryCode

    return (
      <Flex align="center" justify="flex-end" gap="6px" minW={0} maxW="100%" overflow="hidden">
        <Box as="span" flex="0 0 auto" lineHeight={0}>
          <CircleFlag countryCode={countryCode.toLowerCase()} style={{ width: '16px', height: '16px' }} />
        </Box>
        <Text isTruncated fontSize="12px" fontWeight="500" color="solid.800" minW={0} maxW="100%">
          {countryName}
        </Text>
      </Flex>
    )
  }
  if (column.Cell) return <column.Cell row={row} />
  const value = row[column.accessor]
  if (value === null || value === undefined || value === '') return emptyLabel
  if (typeof value === 'object') return String(value?.name ?? value?.title ?? value?.label ?? emptyLabel)
  return String(value)
}

const mobileCardContentSx = {
  '&, & *': {
    boxSizing: 'border-box',
    maxWidth: '100%',
    minWidth: 0,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '& [class*="chakra-stack"], & [class*="chakra-wrap"]': {
    flexWrap: 'wrap',
  },
  '& [class*="chakra-badge"], & [class*="chakra-tag"], & [class*="chakra-button"]': {
    whiteSpace: 'normal',
  },
  '& img, & svg': {
    flexShrink: 0,
  },
  '& [class*="chakra-text"]': {
    minWidth: 0,
    maxWidth: '100%',
  },
  '& .mobile-truncate': {
    display: 'block',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
  },
  '& .mobile-card-detail-value .avatar-cell': {
    width: 'fit-content',
    maxWidth: '100%',
    marginLeft: 'auto',
    justifyContent: 'flex-end',
    gap: '6px',
  },
  '& .mobile-card-detail-value .avatar-cell-text': {
    width: 'auto',
    maxWidth: 'calc(100% - 30px)',
    flex: '0 1 auto',
  },
}

const MasterTable: React.FC<CustomTableProps> = React.memo(({
  columns,
  onRowClick,
  disableMobileRowClick = false,
  data,
  onOpenNewTab,
  onSearch,
  onRowIntent,
  selectedRows,
  onSelectAll,
  onSelectRow,
  allSelected,
  isIndeterminate,
  tableType,
}) => {
  const { t } = useTranslation()
  const profiler = React.useMemo(() => createRenderProfiler(`table:${tableType}`), [tableType])
  const emptyLabel = t('common.labels.na')
  const safeData = Array.isArray(data) ? data : []
  const cardColumns = React.useMemo(
    () =>
      columns
        .filter((column) => column.type !== 'select' && column.type !== 'actions' && !column.hideOnMobileCard)
        .sort((a, b) => (a.mobilePriority ?? 999) - (b.mobilePriority ?? 999)),
    [columns],
  )
  const titleColumn = cardColumns[0]
  const detailColumns = cardColumns.slice(1)

  const handleMobileRowClick = useCallback(
    (row: RowData) => {
      if (!disableMobileRowClick) onRowClick?.(row)
    },
    [disableMobileRowClick, onRowClick],
  )

  const handleMobileRowIntent = useCallback(
    (row: RowData) => {
      if (!disableMobileRowClick) onRowIntent?.(row)
    },
    [disableMobileRowClick, onRowIntent],
  )

  return (
    <React.Profiler id={`table:${tableType}`} onRender={profiler}>
      <Box display={{ base: 'block', md: 'none' }} px="12px" py="10px">
        {safeData.length === 0 ? (
          <Box textAlign="center" color="gray.500" fontWeight="medium" py="48px">
            {t('table.noData')}
          </Box>
        ) : (
          <Flex direction="column" gap="10px">
            {safeData.map((row, index) => {
              return (
                <Box
                  key={row.id ?? `row-${index}`}
                  bg="white"
                  border="1px solid"
                  borderColor="solid.50"
                  borderRadius="10px"
                  boxShadow="0px 1px 3px rgba(0, 0, 0, 0.04)"
                  p="12px"
                  w="100%"
                  maxW="100%"
                  minW={0}
                  overflow="hidden"
                  boxSizing="border-box"
                  cursor={onRowClick && !disableMobileRowClick ? 'pointer' : 'default'}
                  onClick={() => handleMobileRowClick(row)}
                  onMouseEnter={() => handleMobileRowIntent(row)}
                  onFocus={() => handleMobileRowIntent(row)}
                  onPointerDown={() => handleMobileRowIntent(row)}
                >
                  <Box minW={0} maxW="100%" overflow="hidden" sx={mobileCardContentSx}>
                    {titleColumn && (
                      <Box
                        fontSize="14px"
                        fontWeight="700"
                        color="solid.900"
                        lineHeight="20px"
                        minW={0}
                        w="100%"
                        maxW="100%"
                        overflow="hidden"
                      >
                        {renderMobileValue(titleColumn, row, emptyLabel)}
                      </Box>
                    )}
                    {detailColumns.length > 0 && (
                      <Flex direction="column" gap="8px" mt="10px">
                        {detailColumns.map((column) => (
                          <Flex key={column.accessor} align="center" justify="space-between" gap="12px" minW={0} width="100%">
                            <Text
                              fontSize="11px"
                              fontWeight="600"
                              color="solid.400"
                              flex="0 0 42%"
                              maxW="42%"
                              minW={0}
                              lineHeight="16px"
                              overflowWrap="anywhere"
                            >
                              {column.mobileLabel ?? column.label}
                            </Text>
                            <Box
                              className="mobile-card-detail-value"
                              fontSize="12px"
                              fontWeight="500"
                              color="solid.800"
                              textAlign="right"
                              flex="1 1 0"
                              minW={0}
                              maxW="58%"
                              overflow="hidden"
                              sx={mobileCardContentSx}
                            >
                              {renderMobileValue(column, row, emptyLabel)}
                            </Box>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                    </Box>
                </Box>
              )
            })}
          </Flex>
        )}
      </Box>

      <Table display={{ base: 'none', md: 'table' }} width="100%" sx={{ tableLayout: 'fixed' }}>
        <Thead>
          <Tr>
            {columns.map((column) => (
              <Th
                key={column.accessor}
                width={column.width}
                bg="transparent"
                color="solid.400"
                textTransform="none"
                letterSpacing="normal"
                fontSize="12px"
                fontWeight="500"
                borderBottomWidth="1px"
                borderColor="solid.50"
                whiteSpace="normal"
                wordBreak="break-word"
                overflowWrap="anywhere"
                py="10px"
              >
                {column.type === 'select' ? (
                  <Checkbox
                    isChecked={allSelected}
                    isIndeterminate={isIndeterminate}
                    onChange={(e) => onSelectAll(e.target.checked)}
                  />
                ) : (
                  column.label
                )}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {safeData.length === 0 && (
            <Tr>
              <td colSpan={columns.length}>
                <Box textAlign="center" color="gray.500" fontWeight="medium" mt="15%">
                  {t('table.noData')}
                </Box>
              </td>
            </Tr>
          )}
          {safeData.map((row, index) => (
            <TableRowItem
              key={row.id ?? `row-${index}`}
              row={row}
              columns={columns}
              onRowClick={onRowClick}
              onOpenNewTab={onOpenNewTab}
              onSearch={onSearch}
              onRowIntent={onRowIntent}
              isSelected={Array.isArray(selectedRows) ? selectedRows.includes(row.id) : false}
              onSelectRow={onSelectRow}
              tableType={tableType}
            />
          ))}
        </Tbody>
      </Table>
    </React.Profiler>
  )
})

MasterTable.displayName = 'MasterTable'

export default MasterTable
